/**
 * 翻译反馈按钮组件
 * 用于收集用户对自动翻译质量的反馈
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquareWarning, Send, X, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// 反馈类型选项
const FEEDBACK_TYPES = [
  { value: 'inaccurate', label: '翻译不准确', labelEn: 'Inaccurate' },
  { value: 'incomplete', label: '翻译不完整', labelEn: 'Incomplete' },
  { value: 'offensive', label: '内容不当', labelEn: 'Offensive' },
  { value: 'other', label: '其他问题', labelEn: 'Other' },
];

/**
 * 翻译反馈按钮
 * @param {Object} props
 * @param {string} props.contentType - 内容类型 (poster, exhibition, about, etc.)
 * @param {string} props.contentId - 内容ID
 * @param {string} props.fieldName - 字段名 (title, description, etc.)
 * @param {string} props.language - 目标语言
 * @param {string} props.originalText - 原文
 * @param {string} props.translatedText - 翻译后的文本
 * @param {string} props.size - 按钮大小 'sm' | 'md'
 * @param {string} props.variant - 按钮样式 'icon' | 'text'
 */
const TranslationFeedbackButton = ({
  contentType,
  contentId,
  fieldName,
  language,
  originalText,
  translatedText,
  size = 'sm',
  variant = 'icon'
}) => {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [feedbackType, setFeedbackType] = useState('inaccurate');
  const [userComment, setUserComment] = useState('');
  const [suggestedTranslation, setSuggestedTranslation] = useState('');

  const currentLang = i18n.language;
  const isZhOrEn = currentLang === 'zh' || currentLang === 'en';

  // 只在非中英文语言且有翻译文本时显示
  if (isZhOrEn || !translatedText) {
    return null;
  }

  const handleSubmit = async () => {
    if (!token) {
      toast.error(currentLang === 'zh' ? '请先登录后再反馈' : 'Please login to submit feedback');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/translation-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content_type: contentType,
          content_id: contentId,
          field_name: fieldName,
          language: language,
          original_text: originalText,
          translated_text: translatedText,
          feedback_type: feedbackType,
          user_comment: userComment || null,
          suggested_translation: suggestedTranslation || null
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success(
          currentLang === 'zh' 
            ? '感谢您的反馈！我们会尽快处理' 
            : 'Thank you for your feedback!'
        );
        setTimeout(() => {
          setIsOpen(false);
          // 重置表单
          setFeedbackType('inaccurate');
          setUserComment('');
          setSuggestedTranslation('');
        }, 1500);
      } else {
        const data = await response.json();
        toast.error(data.detail || '提交失败');
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      toast.error(currentLang === 'zh' ? '提交失败，请稍后重试' : 'Submission failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {variant === 'icon' ? (
          <button
            className={`inline-flex items-center justify-center text-amber-400 hover:text-amber-600 transition-colors opacity-50 hover:opacity-100 ${
              size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'
            }`}
            title={currentLang === 'zh' ? '报告翻译问题' : 'Report translation issue'}
          >
            <MessageSquareWarning className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
          </button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="text-amber-500 hover:text-amber-700 text-xs gap-1 h-6 px-2"
          >
            <MessageSquareWarning className="w-3 h-3" />
            {currentLang === 'zh' ? '翻译有误？' : 'Translation issue?'}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        {isSubmitted ? (
          <div className="flex flex-col items-center justify-center py-4 text-green-600">
            <Check className="w-8 h-8 mb-2" />
            <p className="text-sm font-medium">
              {currentLang === 'zh' ? '反馈已提交' : 'Feedback submitted'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-amber-800">
                {currentLang === 'zh' ? '报告翻译问题' : 'Report Translation Issue'}
              </h4>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* 当前翻译预览 */}
            <div className="bg-amber-50 rounded-lg p-2 text-xs">
              <p className="text-amber-600 mb-1">
                {currentLang === 'zh' ? '当前翻译：' : 'Current translation:'}
              </p>
              <p className="text-amber-800 line-clamp-2">{translatedText}</p>
            </div>

            {/* 问题类型 */}
            <div className="space-y-2">
              <Label className="text-sm">
                {currentLang === 'zh' ? '问题类型' : 'Issue Type'}
              </Label>
              <RadioGroup
                value={feedbackType}
                onValueChange={setFeedbackType}
                className="grid grid-cols-2 gap-2"
              >
                {FEEDBACK_TYPES.map(type => (
                  <div key={type.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={type.value} id={type.value} />
                    <Label htmlFor={type.value} className="text-xs cursor-pointer">
                      {currentLang === 'zh' ? type.label : type.labelEn}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* 建议翻译 */}
            <div className="space-y-2">
              <Label className="text-sm">
                {currentLang === 'zh' ? '建议翻译（可选）' : 'Suggested translation (optional)'}
              </Label>
              <Textarea
                value={suggestedTranslation}
                onChange={(e) => setSuggestedTranslation(e.target.value)}
                placeholder={currentLang === 'zh' ? '如果您知道正确的翻译...' : 'If you know the correct translation...'}
                rows={2}
                className="text-sm"
              />
            </div>

            {/* 补充说明 */}
            <div className="space-y-2">
              <Label className="text-sm">
                {currentLang === 'zh' ? '补充说明（可选）' : 'Additional comments (optional)'}
              </Label>
              <Textarea
                value={userComment}
                onChange={(e) => setUserComment(e.target.value)}
                placeholder={currentLang === 'zh' ? '其他需要说明的问题...' : 'Any other details...'}
                rows={2}
                className="text-sm"
              />
            </div>

            {/* 提交按钮 */}
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full bg-amber-500 hover:bg-amber-600"
              size="sm"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {currentLang === 'zh' ? '提交反馈' : 'Submit Feedback'}
            </Button>

            {!token && (
              <p className="text-xs text-amber-500 text-center">
                {currentLang === 'zh' ? '请先登录后再提交反馈' : 'Please login to submit feedback'}
              </p>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default TranslationFeedbackButton;
